import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { store } from 'redux-rest-reducer';

import { Text, Separator, Button } from '@cuemath/comps';

import {
  post as createTeacherCred,
  patch as editTeacherCred,
} from 'actions/teacher-cred';
import { invalidateItem as invalidateTeacher } from 'actions/teachers';
import { closeModal } from 'actions/modal';
import {
  Head,
  Footer,
  Message,
  FormWrapper,
  FieldWrapper,
  ButtonWrapper,
  renderFormField,
  getOptions,
} from 'modules/form';

import {
  FORM_ACTIONS,
  FIELD_TYPES,
  teacherCredFields,
  getOnChangeMethod,
  getInputChangeMethod,
  getHeadText,
} from 'constants/form';

class TeacherCredForm extends PureComponent {
  static defaultProps = {
    data: {},
    teachers: null,
    credListOptions: [],
  };

  static propTypes = {
    actions: PropTypes.shape({
      createTeacherCred: PropTypes.func.isRequired,
      editTeacherCred: PropTypes.func.isRequired,
      invalidateTeacher: PropTypes.func.isRequired,
    }).isRequired,
    data: PropTypes.object,
    teachers: PropTypes.object,
    credListOptions: PropTypes.array,
    formType: PropTypes.string.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      errorText: null,
      message: null,
      teacherCredPost: store.post(),
      teacherCredPatch: store.patch(),
      form: this.getFormDefaults(),
      formFields: this.getFormFields(),
      modTeacherCred: false,
    };
    teacherCredFields.forEach(field => {
      const methodName = getOnChangeMethod(field.name);
      this[methodName] = this.onChange.bind(this, field.name);
    });
  }

  getFormDefaults = () => {
    const { data } = this.props;
    const formDefaults = {};

    teacherCredFields.forEach(field => {
      formDefaults[field.name] =
        !!data && data[field.param] ? data[field.param] : field.default;
    });
    return formDefaults;
  };

  getFormFields = () => {
    const { credListOptions } = this.props;

    return teacherCredFields.map(field => {
      if (field.fieldType === FIELD_TYPES.SELECT)
        field.choices = getOptions({ field: field.name, credListOptions });
      return field;
    });
  };

  static getDerivedStateFromProps(props, state) {
    const {
      teachers,
      actions,
      teacherCreds,
      data: { teacher_id: teacherId },
    } = props;
    const { teacherCredPost, teacherCredPatch, modTeacherCred } = state;

    const createTeacherCred = store.post(teacherCreds, teacherCredPost.id);
    const updateTeacherCred = store.patch(teacherCreds, teacherCredPatch.id);
    const teacherGet = store.get(teachers, teacherId, { embed: ['creds', 'psm_info'] });

    const newState = {};

    if (createTeacherCred.resolved && modTeacherCred) {
      if (createTeacherCred.resolved.isCreated) {
        newState.teacherCredPost = createTeacherCred;
        newState.teacherCredData = createTeacherCred.resolved.data;
        newState.message = 'Teacher cred created successfully';

        // Invalidate teacher if not already to show updated teacher cred list
        if (teacherGet && teacherGet.isLoaded && !teacherGet.isStale && modTeacherCred)
          actions.invalidateTeacher(teacherId, { embed: ['creds', 'psm_info'] });
        actions.closeModal();
      } else if (createTeacherCred.resolved.isCreatingFailed) {
        newState.teacherCredPost = createTeacherCred;

        if (store.getError(createTeacherCred.resolved))
          newState.errorText = store.getError(createTeacherCred.resolved).message;
        else newState.errorText = 'Something went wrong';
      }
      newState.modTeacherCred = false;
    }
    if (updateTeacherCred.resolved && modTeacherCred) {
      if (updateTeacherCred.resolved.isPatched) {
        newState.teacherCredData = updateTeacherCred.resolved.data;
        newState.message = 'Teacher cred edited successfully';

        // Invalidate teacher if not already to show updated teacher cred list
        if (teacherGet && teacherGet.isLoaded && !teacherGet.isStale && modTeacherCred)
          actions.invalidateTeacher(teacherId, { embed: ['creds', 'psm_info'] });
        actions.closeModal();
      } else if (updateTeacherCred.resolved.isPatchingFailed) {
        newState.errorText = store.getError(updateTeacherCred.resolved).message;
      }
      newState.modTeacherCred = false;
    }

    return newState;
  }

  onChange = (key, value) => {
    const { form, errorText } = this.state;
    const stateChanges = {
      form: {
        ...form,
        [key]: value,
      },
    };
    if (errorText) stateChanges.errorText = null;

    this.setState(() => ({ ...stateChanges }));
  };

  onToggle = event => {
    const { form, errorText } = this.state;
    const key = event.target.name;
    const value = event.target.checked;
    const stateChanges = {
      form: {
        ...form,
        [key]: value,
      },
    };
    if (errorText) stateChanges.errorText = null;
    this.setState(() => ({ ...stateChanges }));
  };

  onOptionChange = (key, value) => {
    const { form, errorText } = this.state;
    const stateChanges = {
      form: {
        ...form,
        [key]: value,
      },
    };
    if (errorText) stateChanges.errorText = null;
    this.setState(() => ({ ...stateChanges }));
  };

  handlePostAction = () => {
    const {
      teacherCredPost: { id, current },
      form,
      formFields,
    } = this.state;
    const { actions } = this.props;

    if (current && current.isCreating) return;
    const data = {};
    formFields.forEach(({ name, param }) => {
      if (param) data[param] = form[name];
    });
    this.setState({ modTeacherCred: true }, () => {
      actions.createTeacherCred(id, data);
    });
  };

  handlePatchAction = () => {
    const {
      teacherCredPatch: { id },
      form,
      formFields,
    } = this.state;
    const { actions, data } = this.props;
    const updatedAttrs = {};

    formFields.forEach(({ name, param }) => {
      if (param && data[param] !== form[name]) updatedAttrs[param] = form[name];
    });

    if (Object.keys(updatedAttrs).length)
      this.setState({ modTeacherCred: true }, () => {
        actions.editTeacherCred(id, data.id, updatedAttrs);
      });
  };

  onSubmit = event => {
    event.preventDefault();
    const { formType } = this.props;

    if (formType === FORM_ACTIONS.CREATE) this.handlePostAction();
    else if (formType === FORM_ACTIONS.EDIT) this.handlePatchAction();
  };

  renderFormHeader = () => {
    const { formType } = this.props;

    return (
      <Head>
        <Text size="xlarge" align="center">
          {getHeadText(formType, 'Teacher Cred')}
        </Text>
      </Head>
    );
  };

  renderFormFields = () => {
    const { formType } = this.props;
    const { formFields, form } = this.state;

    return (
      <FieldWrapper>
        {formFields.map(field => {
          const disabled =
            field.disabled ||
            formType === FORM_ACTIONS.VIEW ||
            (!field.editable && formType === FORM_ACTIONS.EDIT);
          const required = formType === FORM_ACTIONS.CREATE ? field.required : false;
          const placeholder = disabled ? '' : field.label;

          return renderFormField({
            ...field,
            value: form[field.name],
            onChange: this[getInputChangeMethod(field)],
            required,
            disabled,
            placeholder,
          });
        })}
      </FieldWrapper>
    );
  };

  renderFormFooter = formType => (
    <Footer>
      <ButtonWrapper>
        <Button label="Save" disabled={formType === FORM_ACTIONS.VIEW} />
      </ButtonWrapper>
    </Footer>
  );

  render() {
    const { message, formType } = this.props;
    const { errorText } = this.state;

    return (
      <form onSubmit={this.onSubmit}>
        <FormWrapper>
          {this.renderFormHeader()}
          {message && <Message>{message}</Message>}
          <Separator transparent height="25" />
          {this.renderFormFields()}
          {errorText && <Message error={true}>{errorText}</Message>}
          <Separator transparent height="50" />
          {this.renderFormFooter(formType)}
          <Separator transparent height="50" />
        </FormWrapper>
      </form>
    );
  }
}

export default connect(
  ({ credList, teacherCreds, teachers }) => ({
    credListOptions: store.getAll(credList).data,
    teacherCreds,
    teachers,
  }),
  dispatch => ({
    actions: bindActionCreators(
      {
        createTeacherCred,
        editTeacherCred,
        invalidateTeacher,
        closeModal,
      },
      dispatch,
    ),
  }),
)(TeacherCredForm);
